import { useEffect, useState } from 'react';
import AssistantUsageResource from 'Resources/AssistantUsage';

export type AssistantPermissions = {
	canCreateAgents: boolean;
	canAccessWorkspace: boolean;
	/** False while an admin has automatic routines switched off for the account. */
	routinesEnabled: boolean;
	/** False while an admin has pre-meeting briefs switched off, which the meeting routine's row says. */
	preMeetingBriefEnabled: boolean;
	/** False while an admin has the post-meeting agent switched off. */
	postMeetingAgentEnabled: boolean;
};

const DEFAULT_PERMS: AssistantPermissions = {
	canCreateAgents: false,
	canAccessWorkspace: false,
	routinesEnabled: true,
	preMeetingBriefEnabled: true,
	postMeetingAgentEnabled: true
};

let cached: AssistantPermissions | null = null;
let inflight: Promise<AssistantPermissions> | null = null;

/**
 * Cache the result for the lifetime of the page so the sidebar and the
 * workspace route don't fire two parallel network calls on first paint.
 * The cache is intentionally module-scoped; permissions can only change via
 * the admin UI, which clears it (clearAssistantPermissionsCache).
 */
function loadPermissions(): Promise<AssistantPermissions> {
	if (cached) return Promise.resolve(cached);
	if (inflight) return inflight;
	inflight = AssistantUsageResource.getMyPermissions()
		.then(p => {
			cached = {
				canCreateAgents: Boolean(p?.canCreateAgents),
				canAccessWorkspace: Boolean(p?.canAccessWorkspace),
				// Absent from an older API: nothing is switched off then.
				routinesEnabled: p?.routinesEnabled !== false,
				preMeetingBriefEnabled: p?.preMeetingBriefEnabled !== false,
				postMeetingAgentEnabled: p?.postMeetingAgentEnabled !== false
			};
			return cached;
		})
		.catch(() => DEFAULT_PERMS)
		.finally(() => {
			inflight = null;
		});
	return inflight;
}

/** Drop the cached answer after the admin changes one, so the next screen that mounts asks again. */
export function clearAssistantPermissionsCache(): void {
	cached = null;
}

/**
 * `null` while the request is in flight on first paint — callers should treat
 * `null` as "unknown" and avoid flashing the workspace entry point until the
 * server has answered.
 */
export const useAssistantPermissions = (): AssistantPermissions | null => {
	const [perms, setPerms] = useState<AssistantPermissions | null>(cached);
	useEffect(() => {
		if (cached) {
			setPerms(cached);
			return;
		}
		let cancelled = false;
		loadPermissions().then(next => {
			if (!cancelled) setPerms(next);
		});
		return () => {
			cancelled = true;
		};
	}, []);
	return perms;
};
